"use strict";

const googleClientId = document.currentScript.dataset.googleClientId;
const authorizationUrl = `${window.location.origin}/?success_url=${window.location.href}#prijava`;
const offerUrl = `${document.currentScript.dataset.offerUrl}?success_url=${window.location.href}#pianoLandingOfferInline`;
const allowedReferrers = window.googleSubscribePlans = JSON.parse(document.getElementById("extendedAccessAllowedReferrers").textContent);
let isDebug = document.currentScript.dataset.isDebug === "True";

/**
 * Declare debug state if passed as query parameter, else use `data-is-debug`
 * attribute from script declaration in html file.
 */
const isDebugEnabledFromQuery = new URLSearchParams(window.location.search).get('DEBUG');
if (isDebugEnabledFromQuery) {
  const queryValue = isDebugEnabledFromQuery.toLowerCase();
  isDebug = isDebug === false ? queryValue === 'true' : queryValue !== 'false';
}

/**
 * Constructs and returns the current user's access state based on profile and subscription
 * information.
 *
 * This function retrieves the user's profile data using window-level helpers and determines
 * access rights. If the user is a direct subscriber, it sets access as granted and annotates
 * the grant reason and subscription timestamp.
 */
const getUserState = () => {
  const userState = {
    granted: false
  };
  const userProfile = window.getProfileData();
  if (userProfile) {
    userState.id = window.getUserPPID(userProfile.id);
    userState.registrationTimestamp = window.dayjs(userProfile.date_joined).unix();
    if (userProfile.piano_subscription_type === "subscriber") {
      userState.granted = true;
      userState.grantReason = "SUBSCRIBER";
      userState.subscriptionTimestamp = window.dayjs(userProfile.piano_subscription_start_date).unix();
    }
  }
  return userState;
};
function InitGaaMetering() {
  /**
   * This promise asynchronously retrieves the user's profile data and constructs the access state
   * object by invoking `getUserState()`.
   * The resolved value contains permission and metadata
   * details, which are useful for downstream entitlement integrations into Google Extended
   * Access workflow.
   */
  const publisherEntitlementPromise = new Promise(resolve => {
    const userState = getUserState();
    if (isDebug) console.info("[Google Extended Access] userState:", userState);
    resolve(userState);
  });

  /**
   * Get the information for the user who has just registered.
   * Send that information to your Registration endpoint to register the user 
   * and return the userState for the newly registered user.
   */
  const registerUserPromise = new Promise(resolve => {
    GaaMetering.getGaaUserPromise().then(gaaUser => {
      window.socialLogin('Google', gaaUser.credential);
    });
  });

  /**
   * Remove register intervention upon promise, 
   * Open a login window for existing users to login.
   */
  const handleLoginPromise = new Promise(resolve => {
    GaaMetering.getLoginPromise().then(() => {
      GaaMeteringRegwall.remove();
      window.openModalLogin();
      document.addEventListener('UserLoggedIn', () => {
        window.showGeneralLoader();
      });
    });
  });

  /**
   * Handles unlocking of article content based on user entitlement or subscription status.
   *
   * First checks if the user qualifies as premium using `window.isUserPremium()`.
   * If so, it dispatches a `pianoUnlockContent` event to unlock content. If not, but the user
   * qualifies for access via Google metering (as determined by
   * `entitlementResponse.enablesThisWithGoogleMetering()`), it retrieves the corresponding
   * entitlement and logs the access event. The function then consumes the entitlement and
   * dispatches a `meteringUnlockContent` event, including the Google metering key in the event
   * detail payload.
   */
  const handleUnlockArticle = entitlementResponse => {
    if (window.isUserPremium()) {
      document.body.dispatchEvent(new Event("pianoUnlockContent"));
    } else if (entitlementResponse.enablesThisWithGoogleMetering()) {
      const entitlement = entitlementResponse.getEntitlementForThis();
      if (isDebug) {
        console.info("[Google Extended Access] User gets unlocked content via EA metering. Entitlement: ", entitlement);
      }
      entitlementResponse.consume(() => {
        document.body.dispatchEvent(new CustomEvent("meteringUnlockContent", {
          detail: {
            google_metering_key: entitlement.subscriptionToken
          }
        }));
      });
    }
  };

  /**
   * Just show loader and reload location to get expected state.
   */
  const handleShowPaywall = () => {
    window.showGeneralLoader();
    setTimeout(() => {
      window.location.href = offerUrl;
    });
  };

  /**
   * Reset the subscriptions in order not to interfere with Piano scripts.
   * Gets user state and then initializes Extended access metering
   * with methods and promises declared above.
   */
  const runInitialization = () => {
    (self.SWG = self.SWG || []).push(subscriptions => {
      subscriptions.reset();
    });
    const userState = getUserState();
    const GaaMeteringConfig = {
      shouldInitializeSwG: false,
      googleApiClientId: googleClientId,
      userState: userState,
      allowedReferrers: allowedReferrers,
      handleLoginPromise: handleLoginPromise,
      publisherEntitlementPromise: publisherEntitlementPromise,
      unlockArticle: handleUnlockArticle,
      showPaywall: handleShowPaywall,
      registerUserPromise: registerUserPromise
    };
    GaaMetering.init(GaaMeteringConfig);
    if (isDebug) console.info("[Google Extended Access]: EA initialized", GaaMeteringConfig);
  };

  /**
   * Initial list of promises that are necessary to wait for
   * initialization of Extended access. Base data is always waited
   * and if there is user token, which indicates that user is logged
   * in. Promise for setLoggedIn event is added to `waitFor` list.
   * At the end list of promises is waited in order to call 
   * runInitialization method that triggers Extended Access
   * initialization.
   */
  const waitFor = [new Promise(function (resolve) {
    document.addEventListener('BaseDataLoaded', resolve, false);
  })];
  if (window.getUserToken()) {
    waitFor.push(new Promise(function (resolve) {
      document.addEventListener('setLoggedIn', resolve, false);
    }));
  }
  Promise.all(waitFor).then(function () {
    runInitialization();
  });
  (self.SWG = self.SWG || []).push(function (subscriptions) {
    subscriptions.getEventManager().then(manager => {
      manager.registerEventListener(event => {
        if (event.isFromUserAction) {
          window.googleSendEvent('RRM:E', 'Extended Access', event);
        }
        if (isDebug) console.info("[Google Extended Access]", event);
      });
    });
  });
}
;